<script setup lang="ts">
import { Button } from '@/components/ui/button';
import { getUserCallback, type UserController } from '@/lib/util/store';
import type { PatreonTierInfo } from '@/types/pricing';
import { CloudOff } from 'lucide-vue-next';
import { computed, onMounted, ref } from 'vue';

import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle
} from '@/components/ui/dialog';
import { getConnectablePlatform } from '@/info/connectable_platforms';
import ConnectForm from '../connect/ConnectForm.vue';

const props = defineProps<{
    user?: UserController;
    tier: PatreonTierInfo;
}>()

const toFetchUser = !props.user;

const user = ref(props.user);
const isLoading = ref(toFetchUser);
const isOffline = ref(false);

const isConnectDialogueOpen = ref(false);
const currentPath = ref<string | undefined>(undefined);

const userTierId = computed(() => user.value?.currentTier.id);
const userSubbed = computed(() => userTierId.value === props.tier.id);

const isFreeTier = props.tier.id === '0';
const patreonLink = 'https://patreon.com/AlfredBot';

onMounted(() => {
    // fetch user
    if (toFetchUser) getUserCallback((_user, err) => {
        isLoading.value = false;
        user.value = _user;

        if (err && err == "offline") isOffline.value = true;
        else if (err) console.error(err);
    }, { minified: false });

    // specify current path
    currentPath.value = window.location.pathname + window.location.search + window.location.hash;
})

</script>

<template>
    <template v-if="isLoading">
        <div class="tw-w-24 tw-h-8 tw-rounded tw-bg-gray-700/50 tw-animate-pulse"></div>
    </template>
    <template v-else>
        <!-- LOGIN DIALOGUE POPUP -->

        <Dialog :open="isConnectDialogueOpen" @update:open="isConnectDialogueOpen = $event">
            <DialogContent class="tw-max-w-sm">
                <DialogHeader>
                    <DialogTitle> Subscription Prep </DialogTitle>
                    <DialogDescription class="tw-mt-3 tw-mb-2">
                        In order to subscribe to ButlerBot, you must complete the following quick steps.
                    </DialogDescription>
                </DialogHeader>
                <ConnectForm :platform="getConnectablePlatform('patreon')" :user="user" :fetch-user="false"
                    :is-offline="isOffline" />
            </DialogContent>
        </Dialog>

        <!-- SERVER OFFLINE -->
        <Button v-if="isOffline" variant="destructive_outline_text" disabled>
            <CloudOff />
            Offline
        </Button>
        <!-- SUBSCRIBE BUTTONS -->
        <Button v-else-if="userSubbed && !isFreeTier" variant="destructive_outline_text" as-child>
            <a :href="patreonLink">Unsubscribe</a>
        </Button>
        <Button v-else-if="isFreeTier" variant="default_outline_text" disabled>
            Default
        </Button>
        <Button v-else-if="user && !user.patreonProfile" variant="success_outline_text"
            @click="isConnectDialogueOpen = true">
            Subscribe
        </Button>
        <Button v-else-if="user" variant="success_outline_text" as-child>
            <a :href="`/subscribe/${tier.id}/${tier.name}`">Subscribe</a>
        </Button>
        <Button v-else-if="!user" variant="success_outline_text" @click="isConnectDialogueOpen = true">
            Subscribe
        </Button>
    </template>
</template>

<style scoped></style>